/** Canonical default labels for progressive payment term boxes (0-based index). */
export const PPT_PAYMENT_TRANCHE_LABELS = [
  "Upon confirmation",
  "Prior to delivery",
  "Upon handover, or 3 months from last delivery, whichever is first",
] as const;

export function defaultPptPaymentTrancheLabel(index: number): string {
  if (index >= 0 && index < PPT_PAYMENT_TRANCHE_LABELS.length) {
    return PPT_PAYMENT_TRANCHE_LABELS[index]!;
  }
  return "";
}

/** Legacy / placeholder labels safe to replace when auto-filling a newly added box. */
const REPLACEABLE_PAYMENT_TRANCHE_LABELS = new Set([
  "",
  "Stage",
  "Stage label",
  "Tranche 1",
  "Tranche 2",
  "Tranche 3",
  "Tranche 4",
  "Tranche 5",
  "Confirmation",
  "Upon confirmation",
  "Prior to Delivery",
  "Prior to delivery",
  "Upon completion",
  "Upon Completion",
  "Upon Handover",
  "Upon handover",
  "Full payment",
  ...PPT_PAYMENT_TRANCHE_LABELS,
]);

/** True when the stage text has not been meaningfully customised by the user. */
export function shouldAutoFillPptPaymentTrancheLabel(currentStage: string | null | undefined): boolean {
  const trimmed = String(currentStage ?? "").trim();
  if (!trimmed) return true;
  return REPLACEABLE_PAYMENT_TRANCHE_LABELS.has(trimmed);
}

/**
 * Label for a payment box at `index` when adding/enabling a row.
 * Preserves `currentStage` when the user has already edited it.
 */
export function resolvePptPaymentTrancheLabelForAdd(
  index: number,
  currentStage?: string | null
): string {
  if (currentStage != null && !shouldAutoFillPptPaymentTrancheLabel(currentStage)) {
    return currentStage.trim();
  }
  const label = defaultPptPaymentTrancheLabel(index);
  return label || "Stage";
}

export function createDefaultPptPaymentTermRows(count: number): Array<{ stage: string; percentage: string }> {
  const n = Math.max(1, Math.min(count, 5));
  if (n === 1) {
    return [{ stage: defaultPptPaymentTrancheLabel(0), percentage: "100" }];
  }
  const even = Math.floor(100 / n);
  const remainder = 100 - even * (n - 1);
  return Array.from({ length: n }, (_, idx) => ({
    stage: defaultPptPaymentTrancheLabel(idx) || "Stage",
    percentage: String(idx === n - 1 ? remainder : even),
  }));
}
